import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Avatar,
  Box,
  Grid,
  Chip,
  Button,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  InputAdornment,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  // School as SchoolIcon,
  PersonAdd as PersonAddIcon,
  // Message as MessageIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  Language as WebsiteIcon,
  Link as LinkIcon,
  Search,
  DateRange,
  Clear,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { profileService, ProfileData } from '../../services';
import { followService, FollowRequestItem } from '../../services/follow/followService';
import { ConfirmDialog } from '../../components';
import { CanvasPdfViewer } from '../../components/dashboard/feed/CanvasPdfViewer';
import { useAppDispatch, useAppSelector, RootState } from '@/store';
import { followUser, unfollowUser, cancelFollowRequest, fetchFeedTypes } from '@/store';
import { ContentWithReadMore } from '@/components/dashboard/feed';
import { getHtmlForDisplay } from '../dashboard';
import { FeedList } from '@/components/dashboard/FeedList';
import { Moment } from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incomingRequest, setIncomingRequest] = useState<FollowRequestItem | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [respondingToRequest, setRespondingToRequest] = useState(false);
  const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState(0);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);

  // Get feed types from store
  const { feedTypes, isLoading: feedTypesLoading, error: feedTypesError } = useAppSelector((state: RootState) => state.feed);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value);
  };

  const openDateDialog = () => {
    setDateDialogOpen(true);
  };

  const closeDateDialog = () => {
    setDateDialogOpen(false);
  };

  const applyDateFilter = () => {
    closeDateDialog();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setStartDate(null);
    setEndDate(null);
  };

  const getDateRangeLabel = () => {
    if (startDate && endDate) {
      return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;
    } else if (startDate) {
      return `From ${startDate.format('MMM D')}`;
    } else if (endDate) {
      return `Until ${endDate.format('MMM D')}`;
    }
    return '';
  };

  const hasActiveFilters = searchQuery || selectedType !== 'all' || startDate || endDate;

  useEffect(() => {
    if (userId) {
      loadProfile(userId);
      checkForIncomingRequest(userId);
    }
  }, [userId]);

  // Fetch feed types on mount
  useEffect(() => {
    dispatch(fetchFeedTypes());
  }, [dispatch]);

  const loadProfile = async (id: string, isloading:boolean=true) => {
    try {
      setLoading(isloading);
      setError(null);
      const response = await profileService.getProfile(id);
      console.log(response)
      if (response.success) {
        setProfile(response.data.profile);
      } else {
        setError('Failed to load profile');
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!profile || !userId) return;

    if (profile?.relationshipStatus?.isFollowing) {
      // Unfollow user
      const result = await dispatch(unfollowUser(userId));
      if (result.meta.requestStatus === 'fulfilled') {
        await loadProfile(userId, false);
        await checkForIncomingRequest(userId);
      }
    } else {
      // Follow user
      const result = await dispatch(followUser({ recipientId: userId }));
      if (result.meta.requestStatus === 'fulfilled') {
        await loadProfile(userId, false);
        await checkForIncomingRequest(userId);
      }
    }
  };

  const handleCancelRequest = async () => {
    if (!userId) return;

    const result = await dispatch(cancelFollowRequest({ recipientId: userId }));
    if (result.meta.requestStatus === 'fulfilled') {
      await loadProfile(userId, false);
      await checkForIncomingRequest(userId);
    }
  };

  const handleWhatsAppChat = (whatsappNumber: string) => {
    if (whatsappNumber) {
      const cleanNumber = whatsappNumber.replace(/[^\d]/g, '');
      const url = `https://web.whatsapp.com/send?phone=${cleanNumber}&text=Hi👋`;
      window.open(url, '_blank');
    }
  };

  const checkForIncomingRequest = async (profileUserId: string) => {
    try {
      const response = await followService.getFollowRequests({
        type: 'received',
        page: 1,
        limit: 50, // Get enough to find the specific request
      });

      if (response.success) {
        const request = response.data.requests.find(
          req => req.requester._id === profileUserId && req.status === 'pending'
        );
        setIncomingRequest(request || null);
      }
    } catch (error) {
      console.error('Failed to check for incoming requests:', error);
    }
  };

  const handleAcceptRequest = async () => {
    if (!incomingRequest) return;

    try {
      setRespondingToRequest(true);
      const response = await followService.respondToRequest(incomingRequest._id, {
        response: 'accepted'
      });

      if (response.success) {
        await loadProfile(userId!, false);
        await checkForIncomingRequest(userId!);
        setIncomingRequest(null);
      }
    } catch (error) {
      console.error('Failed to accept request:', error);
    } finally {
      setRespondingToRequest(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!incomingRequest) return;

    try {
      setRespondingToRequest(true);
      const response = await followService.respondToRequest(incomingRequest._id, {
        response: 'rejected'
      });

      if (response.success) {
        await loadProfile(userId!, false);
        await checkForIncomingRequest(userId!);
        setIncomingRequest(null);
        setRejectDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to reject request:', error);
    } finally {
      setRespondingToRequest(false);
    }
  };

  // const handleMessage = () => {
  //   // TODO: Implement messaging functionality
  // };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={40} />
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {error || 'Profile not found'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  const { user, stats, relationshipStatus } = profile;

  const getSocialMediaInfo = (url: string) => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('facebook.com')) {
      return { icon: FacebookIcon, color: '#1877F2', name: 'Facebook' };
    } else if (lowerUrl.includes('linkedin.com')) {
      return { icon: LinkedInIcon, color: '#0A66C2', name: 'LinkedIn' };
    } else if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) {
      return { icon: TwitterIcon, color: '#1DA1F2', name: 'Twitter' };
    } else if (lowerUrl.includes('instagram.com')) {
      return { icon: InstagramIcon, color: '#E4405F', name: 'Instagram' };
    } else {
      return { icon: WebsiteIcon, color: '#666', name: 'Website' };
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* Main Profile Section */}
        <Grid size={{ xs: 12 }}>
          <Paper
            sx={{
              mb: 3,
              borderRadius: 4,
              overflow: 'visible',
              position: 'relative'
            }}
          >
            {/* Cover Photo Section */}
            <Box
              sx={{
                height: { xs: 150, sm: 200, md: 240 },
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
                backgroundImage: 'url(https://s3.hrtalky.com/hr-api-files/1764846884795-498a30cdf23c8915-9.png?w=1200&q=80)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                borderRadius: { xs: '8px 8px 0 0', md: '16px 16px 0 0' },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1,
                },
              }}
            />

            {/* Profile Picture - Positioned half in cover */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 90, sm: 130, md: 170 },
                left: { xs: '50%', sm: '50%', md: 40 },
                transform: { xs: 'translateX(-50%)', sm: 'translateX(-50%)', md: 'none' },
                zIndex: 10,
              }}
            >
              {user.isLookingForJob ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      left: -8,
                      right: -8,
                      bottom: -8,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      padding: '6px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: theme.palette.background.paper,
                      }}
                    />
                  </Box>
                  <Avatar
                    src={user.profilePictureId?.url || undefined}
                    sx={{
                      width: { xs: 90, sm: 120, md: 140 },
                      height: { xs: 90, sm: 120, md: 140 },
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '2.5rem' },
                      bgcolor: 'primary.main',
                      position: 'relative',
                      zIndex: 1,
                      border: { xs: '3px solid white', md: '4px solid white' },
                      boxShadow: 3,
                    }}
                  >
                    {user.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: { xs: -5, md: -3 },
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      color: 'white',
                      fontSize: { xs: '8px', sm: '9px', md: '10px' },
                      fontWeight: 700,
                      padding: { xs: '2px 8px', sm: '3px 10px', md: '3px 12px' },
                      borderRadius: { xs: '8px', md: '12px' },
                      whiteSpace: 'nowrap',
                      zIndex: 2,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                    }}
                  >
                    OPEN TO WORK
                  </Box>
                </Box>
              ) : (
                <Avatar
                  src={user.profilePictureId?.url || undefined}
                  sx={{
                    width: { xs: 90, sm: 120, md: 140 },
                    height: { xs: 90, sm: 120, md: 140 },
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '2.5rem' },
                    bgcolor: 'primary.main',
                    border: { xs: '3px solid white', md: '4px solid white' },
                    boxShadow: 3,
                  }}
                >
                  {user.name?.charAt(0)?.toUpperCase()}
                </Avatar>
              )}
            </Box>

            {/* Profile Content */}
            <Box sx={{ pt: { xs: 7, sm: 8, md: 10 }, px: { xs: 2, sm: 3, md: 4 }, pb: { xs: 3, md: 4 }, mt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' }, mt: { xs: 0, md: 0 } }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {user.name}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                  {user.designation}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {user.aboutMe}
                </Typography>

                {/* Profile Stats */}
                <Box sx={{ display: 'flex', gap: { xs: 3, sm: 4 }, mb: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {stats.feedCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      Posts
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {stats.followersCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      Followers
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {stats.followingCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      Following
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  {incomingRequest ? (
                    // Show accept/reject buttons when there's an incoming follow request
                    <>
                      <Button
                        variant="contained"
                        startIcon={<CheckIcon />}
                        disabled={respondingToRequest}
                        onClick={handleAcceptRequest}
                        color="success"
                        size="small"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        disabled={respondingToRequest}
                        onClick={() => setRejectDialogOpen(true)}
                        color="error"
                        size="small"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Reject
                      </Button>
                    </>
                  ) : relationshipStatus?.isFollowing ? (
                    <>
                      {/* <Button
                        variant="outlined"
                        startIcon={<MessageIcon />}
                        onClick={handleMessage}
                      >
                        Message
                      </Button> */}
                      <Button
                        variant="outlined"
                        onClick={handleFollow}
                        size="small"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Unfollow
                      </Button>
                    </>
                  ) : relationshipStatus?.isRequestSent ? (
                    <Button
                      variant="outlined"
                      onClick={handleCancelRequest}
                      size="small"
                      sx={{
                        borderColor: 'error.main',
                        color: 'error.main',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&:hover': {
                          borderColor: 'error.dark',
                          bgcolor: 'error.light',
                        },
                      }}
                    >
                      Cancel Request
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<PersonAddIcon />}
                      onClick={handleFollow}
                      size="small"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Follow
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Contact Information */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
                  component="a"
                  href={`mailto:${user.email}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': {
                      color: 'primary.main',
                      '& .MuiSvgIcon-root': {
                        color: 'primary.main',
                      },
                    },
                  }}
                >
                  <EmailIcon color="action" />
                  <Typography variant="body2">{user.email}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <Box>
                  <Box
                    onClick={() => handleWhatsAppChat(user.whatsappNumber)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: '#25D366',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#20BA5A',
                        transform: 'translateY(-1px)',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <WhatsAppIcon sx={{ color: 'white', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
                      Chat on WhatsApp
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {user.whatsappNumber}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="action" />
                  <Typography variant="body2">{user.city}, {user.state}</Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Social Media Links */}
            {user.socialMediaLinks && user.socialMediaLinks.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Social Media
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {user.socialMediaLinks.map((url, index) => {
                    const { icon: Icon, color, name } = getSocialMediaInfo(url);
                    return (
                      <Box
                        key={index}
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          textDecoration: 'none',
                          color: 'text.primary',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: color,
                            bgcolor: `${color}10`,
                            transform: 'translateY(-2px)',
                            boxShadow: 1,
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 20, color }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {name}
                        </Typography>
                        <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
            }}
          >
            <Tab label="Job Preferences" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Posts" sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>

          {/* Tab Panel 0: Job Preferences */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              {/* Job Preferences */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Job Preferences
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Job Title
                  </Typography>
                  <Typography variant="body1">{user.jobPreferences.jobTitle}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Roles & Responsibilities
                  </Typography>
                  <ContentWithReadMore
                    content={getHtmlForDisplay(user.jobPreferences.jobDescription)}
                    feedId="profile-job-description-main"
                    expandedContent={expandedContent}
                    setExpandedContent={setExpandedContent}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Experience Level
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {user.jobPreferences.experienceLevel}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Notice Period
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {user.jobPreferences.noticeType}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Work Mode
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {user.jobPreferences.workMode}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Job Type
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {user.jobPreferences.jobType}
                  </Typography>
                </Grid>

                {/* Salary Information */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Salary
                  </Typography>
                  <Typography variant="body1">
                    {user.jobPreferences.currentSalary.max} {user.jobPreferences.currentSalary.denomination} {user.jobPreferences.currentSalary.currency} {user.jobPreferences.currentSalary.period}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Expected Salary
                  </Typography>
                  <Typography variant="body1">
                    {user.jobPreferences.expectedSalary.max} {user.jobPreferences.expectedSalary.denomination} {user.jobPreferences.expectedSalary.currency} {user.jobPreferences.expectedSalary.period}
                  </Typography>
                </Grid>
              </Grid>

              {/* Skills */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Skills
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {user.jobPreferences.skills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Preferred Locations */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Preferred Locations
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {user.jobPreferences.location.map((location, index) => (
                    <Chip
                      key={index}
                      label={location}
                      variant="outlined"
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Current Company */}
              {user.currentCompanyInfo && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WorkIcon />
                    Current Company
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {user.currentCompanyInfo.companyname}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.currentCompanyInfo.addressLine1}, {user.currentCompanyInfo.addressLine2}
                    <br />
                    {user.currentCompanyInfo.city}, {user.currentCompanyInfo.state} - {user.currentCompanyInfo.pincode}
                  </Typography>
                </Box>
              )}

              {/* Resume Section */}
              {user.jobPreferences?.resumeId?.url && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Resume
                  </Typography>
                  <CanvasPdfViewer
                    url={user.jobPreferences?.resumeId.url}
                    fileName={`${user.name}_Resume.pdf`}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Tab Panel 1: Posts */}
          {activeTab === 1 && (
            <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
              {/* Filter Section */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                {/* Search */}
                <TextField
                              placeholder="Search posts, jobs, hire me..."

                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  sx={{
                    flex: { xs: 1, sm: 'none' },
                    width: { xs: '100%', sm: 280 },
                    minWidth: { xs: 'auto', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      height: 36,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                {/* Type Filter */}
                <TextField
                  select
                  size="small"
                  value={selectedType}
                  onChange={handleTypeChange}
                  sx={{
                    flex: { xs: 1, sm: 'none' },
                    width: { xs: 'auto', sm: 130 },
                    minWidth: { xs: 100, sm: 130 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      height: 36,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                  disabled={feedTypesLoading || feedTypes?.length === 0}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {feedTypesLoading ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : feedTypesError ? (
                    <MenuItem disabled>Error</MenuItem>
                  ) : (
                    feedTypes?.map((type) => (
                      <MenuItem key={type._id} value={type._id}>
                        {type.name.toLowerCase() === 'jobseeker' ? 'Hire me' : type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                      </MenuItem>
                    ))
                  )}
                </TextField>

                {/* Date Filter */}
                <Button
                  variant="outlined"
                  onClick={openDateDialog}
                  startIcon={<DateRange sx={{ fontSize: 16 }} />}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    height: 36,
                    px: 1.5,
                    borderRadius: 1,
                    borderColor: theme.palette.divider,
                    color: 'text.primary',
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: theme.palette.divider,
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {getDateRangeLabel() || 'Date'}
                </Button>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Tooltip title="Clear filters">
                    <IconButton
                      onClick={handleClearFilters}
                      size="small"
                      sx={{
                        width: 36,
                        height: 36,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        color: 'text.secondary',
                        flexShrink: 0,
                        '&:hover': {
                          color: 'error.main',
                          borderColor: 'error.main',
                        },
                      }}
                    >
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Feed List */}
              <FeedList
                userId={userId}
                searchQuery={searchQuery}
                selectedType={selectedType !== 'all' ? selectedType : undefined}
                startDate={startDate}
                endDate={endDate}
              />
            </Box>
          )}
        </Paper>
      </Box>

      {/* Date Range Filter Dialog */}
      <Dialog open={dateDialogOpen} onClose={closeDateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Filter by Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
                minDate={startDate || undefined}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStartDate(null);
            setEndDate(null);
            closeDateDialog();
          }}>
            Clear
          </Button>
          <Button onClick={closeDateDialog}>Cancel</Button>
          <Button onClick={applyDateFilter} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Request Confirmation Dialog */}
      <ConfirmDialog
        open={rejectDialogOpen}
        title="Reject Follow Request"
        message={`Are you sure you want to reject the follow request from ${profile?.user.name || 'this user'}?`}
        confirmText="Reject"
        cancelText="Cancel"
        onConfirm={handleRejectRequest}
        onCancel={() => setRejectDialogOpen(false)}
        severity="error"
      />
    </Container>
  );
};